import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { cn } from '@/lib/utils'

const HIDDEN: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  opacity: 0,
  pointerEvents: 'none',
}

/** Уже показанные за сессию адреса: при возврате на блок файл берётся из кеша
 *  браузера, и заглушка на этот миг только мигала бы. */
const seen = new Set<string>()

interface Props {
  src: string
  className?: string
  /** Размер и цвет заглушки, пока картинка едет (у картинки до загрузки размера нет). */
  skeletonClassName?: string
  skeletonStyle?: CSSProperties
  /** У картинки на холсте `role` нужен тестам (`getByRole('presentation')`). */
  role?: string
  /** Верить, что показанный раньше адрес готов сразу. На холсте заглушка двигала бы блок, а в
   *  панели новая картинка до отрисовки оставляла пустое место — там выключаем. */
  rememberLoaded?: boolean
}

/**
 * Картинка с пульсирующей заглушкой, пока браузер её скачивает: без неё место
 * пустело до прихода файла, и блок «прыгал». Признак загрузки ключуется адресом —
 * сменили картинку, снова показываем заглушку.
 */
export const ImageWithSkeleton = ({
  src,
  className,
  skeletonClassName,
  skeletonStyle,
  role,
  rememberLoaded = true,
}: Props) => {
  const remembered = rememberLoaded && seen.has(src)
  const [loadedSrc, setLoadedSrc] = useState<string | null>(() => (remembered ? src : null))
  const ref = useRef<HTMLImageElement>(null)
  // Из кеша картинка бывает готова раньше, чем React повесил `onLoad`. До отрисовки: уже
  // готовая картинка не должна мигнуть заглушкой.
  useLayoutEffect(() => {
    if (ref.current?.complete && ref.current.naturalWidth > 0) {
      seen.add(src)
      setLoadedSrc(src)
    }
  }, [src])
  const loaded = loadedSrc === src || remembered
  const markLoaded = () => {
    seen.add(src)
    setLoadedSrc(src)
  }

  return (
    <>
      {loaded ? null : (
        <div
          aria-hidden
          data-testid="image-skeleton"
          className={cn('animate-pulse', skeletonClassName)}
          style={skeletonStyle}
        />
      )}
      <img
        ref={ref}
        src={src}
        alt=""
        role={role}
        className={className}
        // Пока едет — прозрачная поверх заглушки (родитель `relative`): так она
        // грузится и остаётся в дереве доступности, но не занимает места.
        style={loaded ? undefined : HIDDEN}
        onLoad={markLoaded}
        onError={() => setLoadedSrc(src)}
      />
    </>
  )
}
